import React, { useRef, useState, useEffect, Children } from 'react';
import Icon from './Icon';

/**
 * Carousel — native scroll-snap track with arrow buttons and dots.
 * Children are wrapped in fixed-width items (`itemWidth`).
 */
export default function Carousel({ children, ariaLabel, itemWidth = 260, gap = 16, showDots = true, className = '' }) {
  const track = useRef(null);
  const [page, setPage] = useState(0);
  const count = Children.count(children);
  const step = itemWidth + gap;
  const scroll = (dir) => track.current?.scrollBy({ left: dir * step, behavior: 'smooth' });

  useEffect(() => {
    const el = track.current;
    if (!el) return undefined;
    const onScroll = () => setPage(Math.round(el.scrollLeft / step));
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [step]);

  const dots = Math.min(count, 6);
  return (
    <section className={`carousel${className ? ` ${className}` : ''}`} aria-label={ariaLabel} aria-roledescription="carousel">
      <button type="button" className="carousel__arrow carousel__arrow--prev" aria-label="Previous" onClick={() => scroll(-1)}>
        <Icon name="chevron-left" />
      </button>
      <div className="carousel__track" data-testid="carousel-track" ref={track} style={{ '--item-w': `${itemWidth}px`, '--gap': `${gap}px` }}>
        {Children.map(children, (c, i) => (
          <div className="carousel__item" key={i}>{c}</div>
        ))}
      </div>
      <button type="button" className="carousel__arrow carousel__arrow--next" aria-label="Next" onClick={() => scroll(1)}>
        <Icon name="chevron-right" />
      </button>
      {showDots && count > 1 && (
        <div className="carousel__dots" aria-hidden="true">
          {Array.from({ length: dots }).map((_, i) => (
            <span key={i} className={`carousel__dot${i === Math.min(page, dots - 1) ? ' is-active' : ''}`} />
          ))}
        </div>
      )}
    </section>
  );
}
